import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogTrigger,
  IconButton,
  Tooltip,
} from '@civilgrid/ui/components';
import { useRef, useState } from 'react';
import type { ObjectFile } from '~/common/types';
import { fileIconClassMap, isImage, isMedia, isPDF } from './constants';
import PDFViewer from './PDFViewer';

interface FileViewProps {
  onDownloadFile: (file: ObjectFile) => void;
  /** The file whose download is waiting on its signed URL. */
  pendingDownloadId?: string;
  onDeleteFile: ({ id }: { id: string }) => void;
  hasFile: boolean;
  files: ObjectFile[];
  iconSize: number;
  iconStrokeWidth: number;
}

export const FileView = ({ onDownloadFile, pendingDownloadId, onDeleteFile, hasFile, files }: FileViewProps) => {
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef<HTMLElement>(null);
  const deletingRef = useRef(false);

  const mainContent = (file: ObjectFile) => {
    const { type, path } = file;
    if (isMedia(type)) {
      return (
        <>
          {isImage(type) ? (
            <img src={path} alt={file.name} className="max-h-full max-w-full" />
          ) : (
            <video controls src={path} className="max-h-full max-w-full">
              <track kind="captions" />
              Your browser does not support the video tag
            </video>
          )}
        </>
      );
    } else if (isPDF(type)) {
      return (
        <div>
          <div className="flex w-full justify-center">
            <PDFViewer
              fileURL={path}
              setLoading={setLoading}
              pageNum={currentPage}
              setTotalPages={setTotalPages}
              scale={0.7}
            />
          </div>
          <div className="flex items-center justify-center space-x-4 pt-4">
            <IconButton
              icon="chevronLeft"
              variant="secondary"
              size="icon"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              aria-label="Previous page"
            />
            <span>
              {currentPage} of {totalPages}
            </span>
            <IconButton
              icon="chevronRight"
              variant="secondary"
              size="icon"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              aria-label="Next page"
            />
          </div>
        </div>
      );
    } else {
      return <div className="flex w-full justify-center">We are unable to display the information at this time.</div>;
    }
  };

  const getModalContent = (file: ObjectFile) => {
    const { id } = file;
    return (
      <>
        <div className="flex items-center space-x-2 pb-2">
          <IconButton
            icon="download"
            variant="secondary"
            loading={pendingDownloadId === id}
            data-testid={`civildraw-object-attachment-download-${id}`}
            onClick={() => onDownloadFile(file)}
          >
            Download
          </IconButton>

          <DialogClose asChild>
            <IconButton
              icon="trash"
              variant="danger"
              onClick={() => {
                deletingRef.current = true;
                onDeleteFile({ id });
              }}
              data-testid={`civildraw-attachment-delete-${id}`}
            >
              Delete
            </IconButton>
          </DialogClose>
        </div>
        {mainContent(file)}
      </>
    );
  };

  return (
    <section
      ref={listRef}
      tabIndex={-1}
      aria-label="Attachments"
      className={`mt-2 ${hasFile ? 'grid max-h-56 grid-flow-row grid-cols-3 gap-2 overflow-y-auto' : 'h-4'}`}
    >
      {hasFile ? (
        files.map((file, i) => {
          const label = file.name || `attachment ${i + 1}`;
          const { id, type, path, name } = file;
          const extension = path.split('.').pop();
          return (
            <Dialog key={id}>
              <Tooltip asChild sideOffset={5} text={label} delayDuration={0} side="bottom">
                <DialogTrigger asChild>
                  <button
                    type="button"
                    aria-label={`View ${label}`}
                    className="relative block w-full cursor-pointer overflow-clip rounded border border-solid border-grey-30 p-0 text-left shadow-lg focus-ring"
                    data-testid={`civildraw-file-preview-${id}`}
                  >
                    <div
                      className="overflow-clip"
                      onMouseEnter={(e) => {
                        const { target } = e;
                        if (target instanceof HTMLElement) {
                          target.style.transform = 'scale(1.05)';
                          target.style.transitionDuration = '0.5s';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const { target } = e;
                        if (target instanceof HTMLElement) {
                          target.style.transform = 'scale(1)';
                          target.style.transitionDuration = '0.5s';
                        }
                      }}
                    >
                      {isMedia(type) ? (
                        <div className="transition ease-in-out hover:opacity-80 active:opacity-60">
                          {isImage(type) ? (
                            <img
                              src={path}
                              alt={name}
                              className="h-20 w-full cursor-pointer object-cover object-center"
                            />
                          ) : (
                            <video src={path} className="h-20 w-full cursor-pointer object-cover object-center">
                              <track kind="captions" />
                            </video>
                          )}
                        </div>
                      ) : (
                        <div
                          className={`
                          flex h-20 w-full cursor-pointer items-center justify-center object-cover object-center transition ease-in-out hover:opacity-80 active:opacity-60`}
                        >
                          {isPDF(type) ? (
                            <PDFViewer
                              fileURL={path}
                              setLoading={() => {}}
                              pageNum={1}
                              setTotalPages={() => {}}
                              scale={0.1}
                            />
                          ) : (
                            <i
                              className={`${
                                // @ts-expect-error
                                fileIconClassMap[extension]
                              } fa-4x`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </DialogTrigger>
              </Tooltip>
              <DialogContent
                title={file.name || `Attachment ${i + 1}`}
                titleProps={{ className: 'text-base font-bold' }}
                className="z-[110] w-auto max-w-[90vw] max-h-[90vh] rounded-sm"
                overlayClassName="z-[110] bg-black/[0.439]"
                data-testid={`civildraw-attachment-dialog-${id}`}
                // The deleted file's trigger is removed with it, so focus lands on the attachment list instead.
                onCloseAutoFocus={(event) => {
                  if (!deletingRef.current) return;
                  deletingRef.current = false;
                  event.preventDefault();
                  listRef.current?.focus();
                }}
              >
                <DialogBody className="px-8 pt-0 pb-8">{getModalContent(file)}</DialogBody>
              </DialogContent>
            </Dialog>
          );
        })
      ) : (
        <span className="text-xs font-bold text-grey-40">No files uploaded</span>
      )}
    </section>
  );
};
